// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

/**
 * Validation error with field-level details
 */
export const zValidationError = z.object({
    code: z.enum([
        'validation-error'
    ]),
    message: z.string(),
    errors: z.record(z.string(), z.array(z.string()))
});

/**
 * Standard error response
 */
export const zApiError = z.object({
    code: z.string(),
    message: z.string(),
    details: z.optional(z.record(z.string(), z.string()))
});

export const zMarqueeWalkInResolutionsRequest = z.object({
    file: z.string()
});

/**
 * Outcome of a resolution re-import (FR57). Every unresolved row carrying a host ID is accounted for — matched-and-flipped, still without an account, or an unusable file row — nothing is silently dropped.
 */
export const zMarqueeWalkInResolutionResult = z.object({
    matched: z.int(),
    matched_walk_in: z.int(),
    matched_invitee: z.int(),
    still_unresolvable: z.int(),
    unmatched_in_file: z.int()
});

/**
 * Payload to add a walk-in attendee.
 */
export const zMarqueeWalkInRequest = z.object({
    name: z.string(),
    airbnb_host_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    email: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    company: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    salesforce_account_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    is_late: z.optional(z.boolean())
});

/**
 * List filter tab (all/upcoming/past).
 */
export const zMarqueeTimeframe = z.enum([
    'all',
    'upcoming',
    'past'
]);

/**
 * Invitation response carried on the roster row (D3, FR34). Written by the import, the lock and PUT
 * /attendees/{attendeeId}/rsvp. `no_response` is the explicit 'was invited, never answered' state —
 * distinct from the field being absent because the import carried no RSVP column at all.
 */
export const zMarqueeRsvpStatus = z.enum([
    'invited',
    'rsvp_yes',
    'declined',
    'no_response'
]);

/**
 * The operator's RSVP correction. All four dispositions are settable; `null` is not — only an import can leave the column empty.
 */
export const zMarqueeRsvpUpdateRequest = z.object({
    rsvp_status: zMarqueeRsvpStatus
});

/**
 * Resolved-fraction health signal for the Attendees and Day-of check-in screens (FR12, UX-DR4).
 * `resolved / imported` is the babysitter's one glance — because every failure path still writes a
 * row, the fraction drops visibly when resolution collapses. Imported is the roster only (walk-ins
 * are excluded from the baseline); resolved counts fully-ready roster rows. The gap counts mirror the
 * derived statuses that hold a row back.
 */
export const zMarqueeResolvedHealth = z.object({
    imported: z.int(),
    resolved: z.int(),
    resolved_fraction: z.number(),
    healthy_baseline: z.number(),
    is_healthy: z.boolean(),
    unresolved: z.int(),
    missing_host_id: z.int(),
    missing_account: z.int()
});

/**
 * Source of an attendee's host ID. `manual` pins the row so a Refresh never overwrites it.
 */
export const zMarqueeHostIdSource = z.enum([
    'import',
    'manual'
]);

/**
 * Salesforce account level.
 */
export const zMarqueeAccountLevel = z.enum([
    'child',
    'parent'
]);

/**
 * Derived resolution status (no CTA/readiness dimension), computed first-match-wins,
 * usable-account-first: ready → tagged_at_parent → missing_account → unresolved → missing_host_id.
 */
export const zMarqueeAttendeeStatus = z.enum([
    'ready',
    'tagged_at_parent',
    'missing_account',
    'unresolved',
    'missing_host_id'
]);

/**
 * Attendance only — there is no CTA/attribution state.
 */
export const zMarqueeCheckinStatus = z.enum([
    'arrived',
    'no_show',
    'logged_anyway'
]);

/**
 * How the row entered the roster: `import` for a CSV invitee row, `manual` for one an operator created by hand (the pre-work single add, and every walk-in). NOT the same fact as host_id_source, which records where the host ID came from, nor a replacement for is_walk_in.
 */
export const zMarqueeAttendeeOrigin = z.enum([
    'import',
    'manual'
]);

/**
 * Full-funnel disposition of an attendee (D3, FR34) — **derived on read, never stored**. Eight
 * mutually exclusive lanes, computed first-match-wins in this order:
 * walk_in → late_arrival → checked_in → no_show → rsvp_yes → declined → no_response → invited.
 *
 * Note the narrowness of the `checked_in` lane: because `walk_in` and `late_arrival` are matched
 * first, a present walk-in and a present late arrival land in THEIR lane, not in `checked_in`.
 * This lane is therefore NOT the same width as `MarqueeEventListItem.checked_in`, which is the
 * broad attendance-present count and does include both. Same word, two widths — read the doc
 * comment on that field before wiring either number.
 *
 * **What counts as present for the `checked_in` lane:** the same canonical pair as the broad count
 * — check-in status `arrived` **or** `logged_anyway`. `logged_anyway` is presence, not an exception
 * to it; the lane is narrower than the card's number only because walk_in and late_arrival are
 * matched ahead of it, never because it reads presence differently. Stating this so the two numbers
 * cannot disagree for a third reason.
 *
 * The `late_arrival` lane keys on the operator's stored assertion, so it reads that same present
 * pair: a forced `logged_anyway` called late lands in the late lane, not in `checked_in`.
 */
export const zMarqueeFunnelDisposition = z.enum([
    'invited',
    'rsvp_yes',
    'declined',
    'no_response',
    'checked_in',
    'no_show',
    'walk_in',
    'late_arrival'
]);

/**
 * A CTA carried by a single attendee, with its provenance (D1). `code`, `label` and `category`
 * mirror MarqueeCta so the row renders without a second lookup.
 *
 * **Provenance is NOT carried here** — it lives one level up, on the attendee
 * (`MarqueeAttendee.ctas_customized_at` / `ctas_customized_by`). Tailoring is a fact about the
 * attendee's whole set, not about an individual CTA within it, and stamping each element would make
 * two illegal states representable: a partially-stamped array, and — because clearing a set is done
 * by sending an empty array — a deliberately-cleared set that carries no stamp anywhere and is
 * therefore indistinguishable from "never tailored".
 */
export const zMarqueeAttendeeCta = z.object({
    code: z.string(),
    label: z.optional(z.string()),
    category: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

/**
 * An attendee / check-in roster row.
 */
export const zMarqueeAttendee = z.object({
    id: z.string(),
    name: z.string(),
    company: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    email: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    segment: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    language: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    airbnb_host_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    host_id_resolved: z.boolean(),
    host_id_source: z.optional(zMarqueeHostIdSource),
    sf_child_account_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    sf_parent_account_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    account_level_override: z.optional(z.union([
        zMarqueeAccountLevel,
        z.null()
    ])),
    status: zMarqueeAttendeeStatus,
    arrival_time: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ])),
    checkin_status: z.optional(z.union([
        zMarqueeCheckinStatus,
        z.null()
    ])),
    is_walk_in: z.optional(z.boolean()).default(false),
    origin: z.optional(zMarqueeAttendeeOrigin),
    is_unresolvable: z.optional(z.boolean()).default(false),
    unresolvable_reason: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    rsvp_status: z.optional(z.union([
        zMarqueeRsvpStatus,
        z.null()
    ])),
    funnel_status: z.optional(zMarqueeFunnelDisposition),
    late_arrival: z.optional(z.boolean()),
    ctas: z.optional(z.array(zMarqueeAttendeeCta)),
    ctas_customized_at: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ])),
    ctas_customized_by: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    exported_at: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ]))
});

/**
 * Active host→account resolve source that produced the cached account IDs. `salesforce` is Tray-backed (primary); `csv_import` reads an imported resolution snapshot (fallback). Selected by MARQUEE_RESOLVER_SOURCE.
 */
export const zMarqueeDataSource = z.enum([
    'salesforce',
    'csv_import'
]);

/**
 * Attendee rows plus the resolve-source snapshot metadata and the resolved-fraction health signal.
 */
export const zMarqueeAttendeeList = z.object({
    rows: z.optional(z.array(zMarqueeAttendee)),
    data_source: z.optional(zMarqueeDataSource),
    last_refreshed_at: z.optional(z.iso.datetime()),
    health: z.optional(zMarqueeResolvedHealth)
});

/**
 * The attendee board as `POST /events/{eventId}/resolve` answers it — every field of
 * MarqueeAttendeeList, plus the two counters that describe THIS run. The absolute figures live in
 * `health` and do not move with a run; these two are the delta, and they are the only way an
 * operator can tell a re-run that fixed rows from a re-run that changed nothing.
 */
export const zMarqueeResolveRunResult = zMarqueeAttendeeList.and(z.object({
    resolved_this_run: z.int(),
    considered_this_run: z.int()
}));

/**
 * Outcome of a resolution snapshot upload (FR56). Two identities hold on every successful upload. Every data row in the file is written or counted under exactly one rejection reason, so row_count plus the three rejected_* counters equals the number of data rows in the file — silently dropping rows is the failure this endpoint exists to fix, and accepted_without_name is NOT part of that sum because those rows were written and are already inside row_count. And the snapshot moves by exactly what changed, so previous_row_count minus rows_removed plus rows_added equals row_count.
 */
export const zMarqueeResolutionSourceUploadResult = z.object({
    row_count: z.int(),
    previous_row_count: z.int(),
    rows_added: z.int(),
    rows_removed: z.int(),
    rejected_missing_host_id: z.int(),
    rejected_bad_account_prefix: z.int(),
    rejected_duplicate_host_id: z.int(),
    accepted_without_name: z.int(),
    sources: z.array(z.string())
});

/**
 * What the resolution snapshot currently holds (FR56). Reports the table and only the table, so an empty snapshot is reported as empty rather than as the stats of the CSV bundled with the deploy. uploaded_at/uploaded_by are null while the snapshot is still the one seeded at migration.
 */
export const zMarqueeResolutionSourceStatus = z.object({
    row_count: z.int(),
    uploaded_at: z.union([
        z.iso.datetime(),
        z.null()
    ]),
    uploaded_by: z.union([
        z.string(),
        z.null()
    ]),
    sources: z.array(z.string())
});

export const zMarqueeResolutionSourceRequest = z.object({
    file: z.string()
});

/**
 * The roles an admin may hand out through the operator admin API. `admin` is deliberately absent:
 * it is granted and removed by a developer in the database, so a request body carrying it is a 422
 * `validation-error`.
 */
export const zMarqueeAssignableOperatorRole = z.enum([
    'creator',
    'facilitator'
]);

/**
 * Edit a person. Every field is optional but at least one is required; `first_name` and `last_name`
 * are sent together; the email cannot change. Setting `is_market_manager` true needs an `sf_user_id`
 * in the body or already stored, and `sf_user_id: null` is refused while the person is or stays a
 * Market Manager.
 */
export const zMarqueeOperatorUpdate = z.object({
    role: z.optional(zMarqueeAssignableOperatorRole),
    is_market_manager: z.optional(z.boolean()),
    sf_user_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    first_name: z.optional(z.string().min(1).max(100)),
    last_name: z.optional(z.string().min(1).max(100))
});

/**
 * What an operator is allowed to change (FR51). A **creator** sets an event up and closes it out; a
 * **facilitator** works the door; an **admin** can do everything a creator can and also manages the
 * operator roster. The roles are ordered, not disjoint — facilitator < creator < admin — and every
 * operation a lower role may call, a higher role may call too. A creator does not satisfy an
 * admin-only operation: that refusal is 403 `admin-role-required`, while a facilitator refused a
 * creator-only operation gets 403 `creator-role-required`. The admin role is granted by a developer
 * in the database, never through the API.
 *
 * ⚠ **Name collision, disclosed rather than renamed.** The wire value `facilitator` is a ROLE. The
 * column `marquee_check_ins.facilitator` is a different thing entirely: the operator who performed
 * one particular check-in, which the export's OwnerId rules key on. A facilitator-role operator and
 * a check-in's facilitator are unrelated facts that happen to share a word — the customer's wording
 * was kept and the column was not renamed, so reading either one as the other is the mistake to
 * watch for here.
 *
 * ⚠ **A second collision, disclosed on the same principle.** `reviewers.role` (model
 * `LuminateUser`) is a live column carrying the Luminate reviewer's role (`marketing_manager`, …) on
 * the very session object this endpoint reads. It is not this field. A Marquee operator role and a
 * Luminate reviewer role are unrelated facts that share a word; never read one as the other.
 *
 * ⚠ **A third collision.** `admin` is also a value of `reviewers.role` — the role a Luminate account
 * carries across Luminate products (the Luxe tools use `admin` and `luxe_admin`). A Luminate `admin`
 * is not a Marquee admin and vice versa: Marquee permissions are never decided from
 * `reviewers.role`, and neither role is ever written from the other.
 *
 * The role is a **stored column** on the `marquee_operators` roster (story 6.1), not a derivation
 * from an email allowlist as it was in story 6.0. The wire shape did not change when it moved.
 * Changing an operator's role is a database write that takes effect on their next request — there is
 * no cache and no deploy in the path.
 */
export const zMarqueeOperatorRole = z.enum([
    'creator',
    'facilitator',
    'admin'
]);

export const zMarqueeOperatorInvitationResult = z.object({
    sent: z.literal(true),
    activation_link: z.optional(z.string())
});

/**
 * One row of the operator roster as an admin manages it. Deliberately not `MarqueeOperator`, whose
 * `sf_user_id` is permanently null by contract.
 */
export const zMarqueeManagedOperator = z.object({
    id: z.string(),
    email: z.string(),
    name: z.union([
        z.string(),
        z.null()
    ]),
    role: zMarqueeOperatorRole,
    is_market_manager: z.boolean(),
    sf_user_id: z.union([
        z.string(),
        z.null()
    ]),
    active: z.boolean(),
    deactivated_at: z.union([
        z.iso.datetime(),
        z.null()
    ]),
    created_at: z.iso.datetime(),
    activation_pending: z.union([
        z.boolean(),
        z.null()
    ]),
    activation_expires_at: z.union([
        z.iso.datetime(),
        z.null()
    ]),
    upcoming_assigned_events: z.int(),
    last_active_at: z.union([
        z.iso.datetime(),
        z.null()
    ])
});

/**
 * `linked`: the email already had a live Luminate account, which the person keeps using — its role,
 * status and password are untouched. `created`: a `read_only` Luminate account was created and the
 * activation email requested.
 */
export const zMarqueeOperatorAccount = z.enum([
    'linked',
    'created'
]);

export const zMarqueeOperatorCreateResult = z.object({
    operator: zMarqueeManagedOperator,
    account: zMarqueeOperatorAccount,
    activation_link: z.optional(z.string())
});

/**
 * Add a person to the roster. `sf_user_id` must be a Salesforce User id — exactly 18 characters
 * starting `005`; a `001…` Account id is refused — and is required when `is_market_manager` is
 * true. Names are 1–100 characters after trimming.
 */
export const zMarqueeOperatorCreate = z.object({
    email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
    first_name: z.string().min(1).max(100),
    last_name: z.string().min(1).max(100),
    role: zMarqueeAssignableOperatorRole,
    is_market_manager: z.optional(z.boolean()).default(false),
    sf_user_id: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

/**
 * A Marquee operator — the identity behind check-in facilitation, CTA customization and export
 * attribution (D4). Deliberately thin: no credential material and no roster-administration fields
 * are ever returned.
 */
export const zMarqueeOperator = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    sf_user_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    role: zMarqueeOperatorRole,
    permitted: z.array(z.string())
});

/**
 * Compact market (geo) reference resolved onto read models.
 */
export const zMarqueeMarketRef = z.object({
    id: z.string(),
    name: z.string(),
    identifier: z.optional(z.string())
});

/**
 * An assignable Marquee market manager (backs GET /market-managers) — the curated set carrying `is_market_manager` on the marquee_operators roster, matched to their `reviewers` row by email. `id` is the reviewers.id, NOT the operator ULID: that is what every event's assigned_mms already stores and what the export owner cascade resolves through; `name` is the resolved display name, null when the reviewer row carries neither a first nor a last name; `sf_user_id` (SF User ID 005…) is the per-attendee export OwnerId, stored in the Marquee-owned marquee_market_managers table (nullable until manually backfilled — no SF integration).
 */
export const zMarqueeMarketManager = z.object({
    id: z.string(),
    name: z.union([
        z.string(),
        z.null()
    ]),
    email: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    sf_user_id: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

/**
 * Payload to mark a pending walk-in row as permanently unresolvable (audit-trailed). `reason` is
 * optional (deferred: the design's confirmation modal does not yet collect one — see
 * spec/deferred-work.md); `unresolvable_by`/`unresolvable_at` are always stamped server-side
 * regardless, so who/when is never lost even when why is omitted.
 */
export const zMarqueeMarkUnresolvableRequest = z.object({
    reason: z.optional(z.string())
});

/**
 * The operator roster. `next_cursor` is always null: the roster is returned whole.
 */
export const zMarqueeManagedOperatorList = z.object({
    items: z.array(zMarqueeManagedOperator),
    next_cursor: z.union([
        z.string(),
        z.null()
    ])
});

/**
 * Successful login. `token` is the opaque bearer the client sends on every subsequent Marquee call; it is stored hashed server-side and is never re-derivable from this response.
 */
export const zMarqueeLoginResponse = z.object({
    token: z.string(),
    expires_at: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ])),
    operator: zMarqueeOperator
});

/**
 * Credentials for the Marquee operator login (D4). The operator roster is Marquee-owned — these are not Luminate reviewer credentials.
 */
export const zMarqueeLoginRequest = z.object({
    email: z.string(),
    password: z.string()
});

/**
 * Edit a location: any of the fields, at least one, with the same checks as create. `geo_id` can
 * change only while no event points at the location (409 `location-in-use`). A body that changes
 * nothing is a 200 and writes nothing.
 */
export const zMarqueeLocationUpdate = z.object({
    name: z.optional(z.string().min(1).max(100)),
    geo_id: z.optional(z.string().min(1)),
    time_zone: z.optional(z.string().min(1))
});

/**
 * A custom location echoed on an event: just enough to label it. Inactive locations still resolve.
 */
export const zMarqueeLocationRef = z.object({
    id: z.string(),
    name: z.string()
});

/**
 * A custom location (OPSSTD-10099): a named place that is not an Airbnb market, such as
 * "Freeport, ME". It always belongs to a real open market (its parent `market`) and carries its own
 * IANA `time_zone`, which events linked to it display instead of the market's. Locations are never
 * deleted; deactivating one only stops new events from picking it.
 */
export const zMarqueeLocation = z.object({
    id: z.string(),
    name: z.string(),
    market: zMarqueeMarketRef,
    time_zone: z.string(),
    active: z.boolean(),
    event_count: z.int(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime()
});

/**
 * The location list. `next_cursor` is always null: the list is returned whole.
 */
export const zMarqueeLocationList = z.object({
    items: z.array(zMarqueeLocation),
    next_cursor: z.union([
        z.string(),
        z.null()
    ])
});

/**
 * Create a location. `name` is trimmed and must be unused by any active location, ignoring letter
 * case (409 `location-name-taken`). `geo_id` must be an open market (400 `validation-error`, the same
 * answer event create gives). `time_zone` must be a real IANA region zone; fixed offsets such as
 * `+05:00`, `UTC` and `Etc/…` zones are refused (422 `validation-error`), and an accepted zone is
 * stored in its canonical spelling.
 */
export const zMarqueeLocationCreate = z.object({
    name: z.string().min(1).max(100),
    geo_id: z.string().min(1),
    time_zone: z.string().min(1)
});

/**
 * Import summary. Dirty rows are reported in the counts, not rejected.
 *
 * A re-import **replaces** the roster rather than appending to it, but it is not a wipe. Each row in
 * the new file is matched against the row it supersedes **by email** — compared trimmed and
 * lower-cased, the same key the import dedupes on — and on a match the operator's manual work is
 * carried onto the newly created row. Four rules govern what survives:
 *
 * - **The pin outranks the file.** A manually pinned host ID (`host_id_source = manual`) is carried
 * with its cached Salesforce account IDs, even when the new file carries a different
 * `Airbnb Host ID`: a pin means the file was wrong and an operator corrected it. The correction is
 * releasable in-app — `POST /attendees/{attendeeId}/restore-resolution` returns the row to
 * `host_id_source = import`, and the next re-import then takes the file's value.
 * - **Unresolvable marks are carried** together with their audit trail.
 * - **The file's `rsvp_status` wins.** RSVP is never carried, in either direction — a re-import
 * resets that fact wholesale, including to null when the new cell is blank or uninterpretable.
 * - **Hand-added invitees survive.** A row with `origin = manual` that the new file does not list is
 * revived rather than deleted, keeping its own id and its own pin and mark. If the new file does
 * carry its email, the file's row supersedes it instead, so one email never yields two live rows.
 *
 * Rows the new file cannot match are the ones that genuinely start over. The six carry counters below
 * are the report of that decision, per re-import: what survived and what did not. A drop is not
 * necessarily an error — the attendee may simply be gone from the new invitee list — it is the number
 * that makes the loss visible instead of silent. A first-ever import into an empty roster reports all
 * six as zero.
 *
 * Four further fields report what the import chose NOT to act on, and one what it acted on late:
 * `columns_ignored` names back every header cell no column claimed, the three `account_ids_*`
 * skipped/rejected/dropped counters say why a supplied Salesforce account ID was not written, and
 * `account_ids_applied_after_resolve` says how many of the host-ID losers were written anyway once
 * resolution came back empty. A file that carries no account ID column reports the four counters as
 * zero; `columns_ignored` is a list, not a counter, and is non-empty whenever a header went
 * unclaimed regardless of which columns the file carries.
 *
 * `imported` counts the rows taken from the file, so `imported + rows_dropped` is what the file
 * contained. Revived hand-added rows are not part of that count: the roster the import leaves behind
 * is `imported + manual_carried` rows, which is the total the resolution counts below are aggregated
 * over.
 */
export const zMarqueeImportResult = z.object({
    imported: z.optional(z.int()),
    resolved: z.optional(z.int()),
    unresolved: z.optional(z.int()),
    missing_host_id: z.optional(z.int()),
    missing_account: z.optional(z.int()),
    tagged_at_parent: z.optional(z.int()),
    rows_dropped: z.optional(z.int()),
    pins_carried: z.optional(z.int()),
    pins_dropped: z.optional(z.int()),
    marks_carried: z.optional(z.int()),
    marks_dropped: z.optional(z.int()),
    manual_carried: z.optional(z.int()),
    manual_superseded: z.optional(z.int()),
    columns_ignored: z.optional(z.array(z.string())),
    account_ids_skipped_parent_row: z.optional(z.int()),
    account_ids_skipped_host_id_wins: z.optional(z.int()),
    account_ids_rejected_invalid: z.optional(z.int()),
    account_ids_dropped_on_reimport: z.optional(z.int()),
    account_ids_applied_after_resolve: z.optional(z.int())
});

export const zMarqueeImportInviteesRequest = z.object({
    file: z.string()
});

/**
 * A host search hit for the 'Search by name' fix loop (backs GET /hosts).
 */
export const zMarqueeHostSearchResult = z.object({
    airbnb_host_id: z.string(),
    name: z.string(),
    company: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

/**
 * A market row from the monolith's synced `geos` table (backs GET /markets).
 */
export const zMarqueeGeo = z.object({
    id: z.string(),
    name: z.string(),
    identifier: z.string(),
    country: z.optional(z.string()),
    region: z.optional(z.string()),
    status: z.string(),
    time_zone: z.union([
        z.string(),
        z.null()
    ])
});

/**
 * Export sink. Only `csv` (assisted CSV load) is supported this MVP; kept as an enum for forward-compat.
 */
export const zMarqueeExportSink = z.enum([
    'csv'
]);

/**
 * A prior export record.
 */
export const zMarqueeExportRecord = z.object({
    rows: z.optional(z.int()),
    skipped: z.optional(z.int()),
    failed: z.optional(z.int()),
    excluded: z.optional(z.int()),
    fallback_owners: z.optional(z.int()),
    exported_by: z.optional(z.string()),
    exported_at: z.optional(z.iso.datetime()),
    idempotent: z.optional(z.boolean()).default(true),
    filename: z.optional(z.string())
});

/**
 * Result of a successful export run.
 */
export const zMarqueeExportRunResult = z.object({
    sink: z.optional(zMarqueeExportSink),
    record: z.optional(zMarqueeExportRecord)
});

/**
 * Disposition of an export **preview** row: included in the run, skipped, or excluded (FR45).
 *
 * `excluded` here means exactly one thing: **the row's attendee is marked unresolvable**
 * (`MarqueeExportRow.is_unresolvable`) — a terminal, deliberate operator decision with no fix
 * action, rendered as the 'Unresolvable' pill. It is NOT the resolver's internal `excluded`
 * disposition, which flags a present walk-in carrying no host ID (skip reason MISSING_HOST_ID).
 * That resolver case is still fixable (Resolve or Mark unresolvable), still blocks the
 * `no_pending_walk_ins` export gate, and must keep surfacing as `skipped` with its
 * `skip_reason` + `is_pending_walk_in` affordance. The two are different facts: collapsing them
 * would strip the resolve affordance from the exact rows that block the run.
 */
export const zMarqueeExportDisposition = z.enum([
    'included',
    'skipped',
    'excluded'
]);

/**
 * A preview-grid row (display only). This is NOT the emitted CSV schema (PRD §5.1, 9 columns).
 */
export const zMarqueeExportRow = z.object({
    attendee_id: z.optional(z.string()),
    attendee: z.optional(z.string()),
    child_account: z.optional(z.string()),
    host_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    owner_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    dim_subject: z.optional(z.string()),
    dim_call_topics: z.optional(z.string()),
    ctas: z.optional(z.array(zMarqueeAttendeeCta)),
    disposition: z.optional(zMarqueeExportDisposition),
    skip_reason: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    status: z.optional(zMarqueeAttendeeStatus),
    is_pending_walk_in: z.optional(z.boolean()),
    owner_fallback: z.optional(z.boolean()),
    is_unresolvable: z.optional(z.boolean())
});

/**
 * Payload to run the export.
 */
export const zMarqueeExportRequest = z.object({
    sink: zMarqueeExportSink
});

/**
 * Export preview counts.
 */
export const zMarqueeExportPreviewCounts = z.object({
    included: z.optional(z.int()),
    skipped: z.optional(z.int()),
    excluded: z.optional(z.int()),
    failed: z.optional(z.int()),
    pending_walk_ins: z.optional(z.int()),
    fallback_owners: z.optional(z.int()),
    unmapped_call_topics: z.optional(z.array(z.string())),
    collapsed: z.optional(z.int()),
    account_fallback: z.optional(z.int())
});

/**
 * Structural export gate. A false here blocks the run (409); per-row recoverable issues do not appear here.
 */
export const zMarqueeExportGate = z.object({
    ctas_in_cta_library: z.optional(z.boolean()),
    missing_host_excluded: z.optional(z.boolean()),
    no_pending_walk_ins: z.optional(z.boolean())
});

/**
 * Export validation gate + preview rows + last export summary.
 */
export const zMarqueeExportPreview = z.object({
    gate: z.optional(zMarqueeExportGate),
    counts: z.optional(zMarqueeExportPreviewCounts),
    rows: z.optional(z.array(zMarqueeExportRow)),
    last_export: z.optional(z.union([
        zMarqueeExportRecord,
        z.null()
    ]))
});

/**
 * Event format = the Salesforce creator **Type** picklist, pass-through to the export `Type`
 * column (Epic 5). Default `Conference`. "Presentation" is invalid. (SCP-2026-07-03.)
 */
export const zMarqueeEventFormat = z.enum([
    'Call',
    'Email',
    'Meeting',
    'SMS',
    'Other',
    'Conference',
    'Market Visit'
]);

/**
 * How the room is attended (D2, FR50). Marquee-local metadata: **no code branches on it** — it
 * touches neither capture, resolution, lifecycle, gates nor export. Chosen at create (default
 * `in_person`) and freely editable in pre_work, unlike capture_mode.
 *
 * **This is NOT the export `Type` column** — that is `format`, the Salesforce picklist mirror.
 * The two are orthogonal: `event_type` is how people attended, `format` is what Salesforce calls
 * the activity.
 */
export const zMarqueeEventType = z.enum([
    'in_person',
    'virtual'
]);

/**
 * Partial event edit (PATCH) — every field optional, send only what changes. Same fields as
 * MarqueeEventCreate. Accepted only in pre_work (409 otherwise).
 */
export const zMarqueeEventUpdate = z.object({
    name: z.optional(z.string()),
    market_geo_id: z.optional(z.string()),
    date: z.optional(z.iso.date()),
    start_time: z.optional(z.string()),
    end_time: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    format: z.optional(zMarqueeEventFormat),
    event_type: z.optional(z.union([
        zMarqueeEventType,
        z.null()
    ])),
    hashtag: z.optional(z.string().regex(/^#/)),
    sf_campaign_id: z.optional(z.string()),
    ctas: z.optional(z.array(z.string())),
    assigned_mms: z.optional(z.array(z.string())),
    location_id: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

/**
 * Event lifecycle state. Every transition is manual (pre_work → locked → live → closed):
 * lock freezes the invitee set, start makes the event live, closing is manual. There is no
 * auto-start, no auto-close and no reopen.
 */
export const zMarqueeEventState = z.enum([
    'pre_work',
    'locked',
    'live',
    'closed'
]);

/**
 * How an event captures CTA attribution (D2, FR44). `presentation` is the classic blanket-CTA
 * event: one event-level selection is snapshotted onto everyone who checks in. `tune_up` is the
 * 1:1 format where each attendee carries their own tailored CTA set (see PUT
 * /attendees/{attendeeId}/ctas). Chosen at create, defaults to `presentation`, and is
 * **immutable afterwards** — which is why MarqueeEventUpdate deliberately omits the field.
 */
export const zMarqueeCaptureMode = z.enum([
    'tune_up',
    'presentation'
]);

/**
 * Event row for the tracker list.
 */
export const zMarqueeEventListItem = z.object({
    id: z.string(),
    name: z.string(),
    market: zMarqueeMarketRef,
    date: z.iso.date(),
    start_time: z.optional(z.string()),
    end_time: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    time_zone: z.string(),
    time_zone_defaulted: z.optional(z.boolean()),
    location: z.optional(z.union([
        zMarqueeLocationRef,
        z.null()
    ])),
    capture_mode: zMarqueeCaptureMode,
    event_type: z.union([
        zMarqueeEventType,
        z.null()
    ]),
    format: zMarqueeEventFormat,
    state: zMarqueeEventState,
    state_badge: z.optional(z.string()),
    hashtag: z.string(),
    ctas: z.optional(z.array(z.string())),
    invited: z.optional(z.int()),
    gaps_to_resolve: z.optional(z.int()),
    checked_in: z.optional(z.int())
});

/**
 * Paginated envelope for the event list so cursor pagination is a non-breaking addition later.
 */
export const zMarqueeEventList = z.object({
    items: z.array(zMarqueeEventListItem),
    next_cursor: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

/**
 * An assigned MM echoed on the event detail. `id` is the reviewers.id (what MarqueeEventCreate
 * stores and the edit-prefill needs); `name` is the resolved display name so the FE renders MMs
 * without cross-referencing GET /market-managers, falling back to the id when the reviewer row has
 * no name or no longer exists; `email` is the reviewer's email (null when the id has no reviewer
 * row). Resolution ignores role/status (a historically assigned reviewer keeps its details after
 * deactivation).
 */
export const zMarqueeAssignedMm = z.object({
    id: z.string(),
    name: z.string(),
    email: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

/**
 * Resolution tallies for the Attendees tab; also drives the resolved-fraction health indicator.
 */
export const zMarqueeEventCounts = z.object({
    imported: z.optional(z.int()),
    resolved: z.optional(z.int()),
    unresolved: z.optional(z.int()),
    missing_host_id: z.optional(z.int()),
    missing_account: z.optional(z.int()),
    tagged_at_parent: z.optional(z.int())
});

/**
 * Soft completeness warning raised by POST /lock (M8, FR14). Populated only on the lock response
 * when at least one non-walk-in row still lacks BOTH account IDs (child and parent). It is a loud
 * warning, never a hard block — the event locks and advances to locked regardless. `null` when every
 * non-walk-in row carries at least one account ID.
 */
export const zMarqueeCompletenessWarning = z.object({
    incomplete_rows: z.int()
});

/**
 * Event detail with resolution counts and the manual close timestamp. Inherits start_time,
 * end_time, time_zone and format from MarqueeEventListItem (SCP-2026-07-03).
 */
export const zMarqueeEventDetail = zMarqueeEventListItem.and(z.object({
    assigned_mms: z.optional(z.array(zMarqueeAssignedMm)),
    closed_at: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ])),
    sf_campaign_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    counts: z.optional(zMarqueeEventCounts),
    completeness_warning: z.optional(z.union([
        zMarqueeCompletenessWarning,
        z.null()
    ]))
}));

/**
 * Payload to create an event tracker.
 */
export const zMarqueeEventCreate = z.object({
    name: z.string(),
    market_geo_id: z.string(),
    location_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    capture_mode: z.optional(zMarqueeCaptureMode),
    event_type: z.optional(zMarqueeEventType),
    date: z.iso.date(),
    start_time: z.string(),
    end_time: z.optional(z.string()),
    format: z.optional(zMarqueeEventFormat),
    hashtag: z.string().regex(/^#/),
    sf_campaign_id: z.optional(z.string()),
    ctas: z.optional(z.array(z.string()).min(1)),
    assigned_mms: z.optional(z.array(z.string()))
});

/**
 * Payload for the per-attendee CTA set (FR30/31). **Full replacement** of this attendee's CTA codes,
 * in display order — which is what makes PUT /attendees/{attendeeId}/ctas natively idempotent:
 * re-sending the same body is a no-op, and a retry after a dropped response cannot double-apply.
 *
 * Deliberately **no `@minItems`**: full replacement is also how a set is cleared, so an empty array
 * must be legal on the wire. FR30's 'at least one CTA' is a UX affordance on the capture sheet, not
 * a contract constraint. There is likewise **no upper cap** (FR31) — a tune-up conversation may
 * legitimately cover many topics.
 *
 * **Duplicates are collapsed, not rejected** — the same posture as the event-level CTA writes, which
 * dedupe before storing. Sending a code twice is accepted and stored once, so the joined Call Topics
 * cell and the exported topics string never carry a repeat.
 */
export const zMarqueeCtaSetRequest = z.object({
    ctas: z.array(z.string())
});

/**
 * Payload to change the event-level blanket CTA selection mid-event. Full replacement, in display
 * order. Accepted only while the event is live (409 otherwise).
 *
 * **An empty array is how the selection is cleared, and it is conditional on capture mode.** For
 * `capture_mode = tune_up`, `ctas: []` is accepted and clears the blanket selection (lead and
 * secondary both empty) — a real operator intent, since a tune-up captures per attendee. For
 * `capture_mode = presentation` an empty selection is rejected **422 `validation-error`**: the
 * blanket selection is what every later check-in snapshots.
 *
 * TypeSpec cannot make `@minItems` conditional on another field, so the wire constraint is dropped
 * and the per-mode rule moves service-side. **The rejection status is unchanged** — a presentation
 * event with an empty selection answered 422 `validation-error` before the move and answers it
 * after. Only the enforcement layer moved; no consumer keying on 422 is affected.
 *
 * `ctas` stays **required** here: unlike MarqueeEventCreate and MarqueeEventUpdate it is the entire
 * body, so there is no omission channel and `[]` carries the "clear it" meaning instead.
 */
export const zMarqueeCtaChangeRequest = z.object({
    ctas: z.array(z.string())
});

/**
 * A selectable CTA from the library (backs GET /ctas).
 */
export const zMarqueeCta = z.object({
    code: z.string(),
    label: z.string(),
    category: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

/**
 * Attendance the operator can set on check-in (logged_anyway is derived via log_anyway).
 */
export const zMarqueeCheckInAttendance = z.enum([
    'arrived',
    'no_show'
]);

/**
 * Payload to mark attendance.
 */
export const zMarqueeCheckInRequest = z.object({
    status: zMarqueeCheckInAttendance,
    log_anyway: z.optional(z.boolean()).default(false),
    is_late: z.optional(z.boolean())
});

/**
 * Payload to assign a host ID to a missing_host_id row ('Search by name').
 */
export const zMarqueeAssignHostRequest = z.object({
    airbnb_host_id: z.string()
});

/**
 * One invitee-file row, typed instead of uploaded: the fields mirror the ten columns the importer
 * recognises and the service applies the importer's own derivations. Every field is optional because the
 * importer never rejects a row. See v3/domains/marquee/docs/attendee_origin.md.
 */
export const zMarqueeAddAttendeeRequest = z.object({
    name: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    company: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    email: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    account_name: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    airbnb_host_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    host_id_18: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    salesforce_account_id: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    segment: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    language: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    rsvp: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

export const zAssignHostData = z.object({
    body: zMarqueeAssignHostRequest,
    path: z.object({
        attendeeId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zAssignHostResponse = zMarqueeAttendee;

export const zCheckInData = z.object({
    body: zMarqueeCheckInRequest,
    path: z.object({
        attendeeId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zCheckInResponse = zMarqueeAttendee;

export const zUndoCheckInData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        attendeeId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zUndoCheckInResponse = zMarqueeAttendee;

export const zSetAttendeeCtasData = z.object({
    body: zMarqueeCtaSetRequest,
    path: z.object({
        attendeeId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zSetAttendeeCtasResponse = zMarqueeAttendee;

export const zMarkUnresolvableData = z.object({
    body: zMarqueeMarkUnresolvableRequest,
    path: z.object({
        attendeeId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zMarkUnresolvableResponse = zMarqueeAttendee;

export const zRestoreResolutionData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        attendeeId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zRestoreResolutionResponse = zMarqueeAttendee;

export const zRetagChildData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        attendeeId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zRetagChildResponse = zMarqueeAttendee;

export const zSetAttendeeRsvpData = z.object({
    body: zMarqueeRsvpUpdateRequest,
    path: z.object({
        attendeeId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zSetAttendeeRsvpResponse = zMarqueeAttendee;

export const zMarqueeLoginData = z.object({
    body: zMarqueeLoginRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zMarqueeLoginResponse2 = zMarqueeLoginResponse;

export const zMarqueeLogoutData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * There is no content to send for this request, but the headers may be useful.
 */
export const zMarqueeLogoutResponse = z.void();

export const zMarqueeCurrentOperatorData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zMarqueeCurrentOperatorResponse = zMarqueeOperator;

export const zListCtasData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zListCtasResponse = z.array(zMarqueeCta);

export const zListEventsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        search: z.optional(z.string()),
        statuses: z.optional(z.array(zMarqueeEventState)),
        timeframe: z.optional(zMarqueeTimeframe)
    }))
});

/**
 * The request has succeeded.
 */
export const zListEventsResponse = zMarqueeEventList;

export const zCreateEventData = z.object({
    body: zMarqueeEventCreate,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded and a new resource has been created as a result.
 */
export const zCreateEventResponse = zMarqueeEventDetail;

export const zDeleteEventData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * There is no content to send for this request, but the headers may be useful.
 */
export const zDeleteEventResponse = z.void();

export const zGetEventData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zGetEventResponse = zMarqueeEventDetail;

export const zUpdateEventData = z.object({
    body: zMarqueeEventUpdate,
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zUpdateEventResponse = zMarqueeEventDetail;

export const zListAttendeesData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.object({
        q: z.optional(z.string())
    }))
});

/**
 * The request has succeeded.
 */
export const zListAttendeesResponse = zMarqueeAttendeeList;

export const zAddAttendeeData = z.object({
    body: zMarqueeAddAttendeeRequest,
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded and a new resource has been created as a result.
 */
export const zAddAttendeeResponse = zMarqueeAttendee;

export const zCloseEventData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zCloseEventResponse = zMarqueeEventDetail;

export const zChangeBlanketCtaData = z.object({
    body: zMarqueeCtaChangeRequest,
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zChangeBlanketCtaResponse = zMarqueeEventDetail;

export const zRunExportData = z.object({
    body: zMarqueeExportRequest,
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zRunExportResponse = zMarqueeExportRunResult;

export const zExportLatestCsvData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zExportLatestCsvResponse = z.string();

export const zExportHistoryData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zExportHistoryResponse = z.array(zMarqueeExportRecord);

export const zExportPreviewData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zExportPreviewResponse = zMarqueeExportPreview;

export const zImportInviteesData = z.object({
    body: zMarqueeImportInviteesRequest,
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zImportInviteesResponse = zMarqueeImportResult;

export const zLockEventData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zLockEventResponse = zMarqueeEventDetail;

export const zResolveEventData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zResolveEventResponse = zMarqueeResolveRunResult;

export const zStartEventData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zStartEventResponse = zMarqueeEventDetail;

export const zWalkInResolutionsData = z.object({
    body: zMarqueeWalkInResolutionsRequest,
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zWalkInResolutionsResponse = zMarqueeWalkInResolutionResult;

export const zAddWalkInData = z.object({
    body: zMarqueeWalkInRequest,
    path: z.object({
        eventId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded and a new resource has been created as a result.
 */
export const zAddWalkInResponse = zMarqueeAttendee;

export const zSearchHostsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.object({
        q: z.string()
    })
});

/**
 * The request has succeeded.
 */
export const zSearchHostsResponse = z.array(zMarqueeHostSearchResult);

export const zGetInviteeImportTemplateData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zGetInviteeImportTemplateResponse = z.string();

export const zListLocationsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        include_inactive: z.optional(z.boolean())
    }))
});

/**
 * The request has succeeded.
 */
export const zListLocationsResponse = zMarqueeLocationList;

export const zCreateLocationData = z.object({
    body: zMarqueeLocationCreate,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded and a new resource has been created as a result.
 */
export const zCreateLocationResponse = zMarqueeLocation;

export const zUpdateLocationData = z.object({
    body: zMarqueeLocationUpdate,
    path: z.object({
        locationId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zUpdateLocationResponse = zMarqueeLocation;

export const zDeactivateLocationData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        locationId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zDeactivateLocationResponse = zMarqueeLocation;

export const zReactivateLocationData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        locationId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zReactivateLocationResponse = zMarqueeLocation;

export const zListMarketManagersData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zListMarketManagersResponse = z.array(zMarqueeMarketManager);

export const zListMarketsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        q: z.optional(z.string())
    }))
});

/**
 * The request has succeeded.
 */
export const zListMarketsResponse = z.array(zMarqueeGeo);

export const zListOperatorsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zListOperatorsResponse = zMarqueeManagedOperatorList;

export const zCreateOperatorData = z.object({
    body: zMarqueeOperatorCreate,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded and a new resource has been created as a result.
 */
export const zCreateOperatorResponse = zMarqueeOperatorCreateResult;

export const zUpdateOperatorData = z.object({
    body: zMarqueeOperatorUpdate,
    path: z.object({
        operatorId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zUpdateOperatorResponse = zMarqueeManagedOperator;

export const zDeactivateOperatorData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        operatorId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zDeactivateOperatorResponse = zMarqueeManagedOperator;

export const zReactivateOperatorData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        operatorId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zReactivateOperatorResponse = zMarqueeManagedOperator;

export const zResendOperatorInvitationData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        operatorId: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zResendOperatorInvitationResponse = zMarqueeOperatorInvitationResult;

export const zGetResolutionSourceData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zGetResolutionSourceResponse = zMarqueeResolutionSourceStatus;

export const zUploadResolutionSourceData = z.object({
    body: zMarqueeResolutionSourceRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zUploadResolutionSourceResponse = zMarqueeResolutionSourceUploadResult;

export const zExportResolutionSourceData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The request has succeeded.
 */
export const zExportResolutionSourceResponse = z.string();
